import { createRoute, useNavigate } from '@tanstack/react-router';
import { Route as rootRoute } from '../__root';
import { useEffect, useState, FormEvent } from 'react';
import { useAuthStore } from '../../store';
import { auth } from '../../firebase';
import { ShieldAlert, Save, Key, Package, Receipt, Users, MessageSquare, Clock } from 'lucide-react';
import { ProductsManager } from '../../components/admin/ProductsManager';
import { TransactionHistory } from '../../components/admin/TransactionHistory';
import { VendorsManager } from '../../components/admin/VendorsManager';
import { VendorRequestsManager } from '../../components/admin/VendorRequestsManager';
import { Messaging } from '../../components/Messaging';
import { Helmet } from 'react-helmet-async';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminDashboard,
});

type Tab = 'products' | 'transactions' | 'vendors' | 'requests' | 'messages' | 'config';

function AdminDashboard() {
  const { profile, isAuthReady } = useAuthStore();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('products');
  const [stripeSecret, setStripeSecret] = useState('');
  const [stripePublic, setStripePublic] = useState('');
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
  const [preferMockPayments, setPreferMockPayments] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    const handleStartSupportChat = async (e: any) => {
      const { targetId, targetName, role } = e.detail;
      if (!auth.currentUser) return;
      try {
        const { getDocs, query, collection, where, addDoc, serverTimestamp } = await import('firebase/firestore');
        const { db } = await import('../../firebase');
        let q;
        if (role === 'customer') {
          q = query(
            collection(db, 'chat_threads'), 
            where('vendorId', '==', 'ADMIN'),
            where('customerId', '==', targetId)
          );
        } else {
          q = query(
            collection(db, 'chat_threads'), 
            where('customerId', '==', 'ADMIN'),
            where('vendorId', '==', targetId)
          );
        }
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
          const threadData = {
            customerId: role === 'customer' ? targetId : 'ADMIN',
            customerName: role === 'customer' ? targetName : 'Platform Support',
            vendorId: role === 'vendor' ? targetId : 'ADMIN',
            vendorName: role === 'vendor' ? targetName : 'Platform Support',
            lastMessage: '',
            lastMessageAt: serverTimestamp(),
            readStatus: {
              [auth.currentUser.uid]: true,
              'ADMIN': true,
              [targetId]: true
            }
          };
          await addDoc(collection(db, 'chat_threads'), threadData);
        }
        setActiveTab('messages');
      } catch (e) {
        console.error(e);
      }
    };
    
    window.addEventListener('start-support-chat', handleStartSupportChat as any);
    return () => window.removeEventListener('start-support-chat', handleStartSupportChat as any);
  }, []);

  useEffect(() => {
    if (isAuthReady && (!profile || !profile.isAdmin)) {
      navigate({ to: '/login' });
    }
  }, [profile, isAuthReady, navigate]);

  useEffect(() => {
    const fetchConfig = async () => {
      if (!auth.currentUser) return;
      try {
        const token = await auth.currentUser.getIdToken();
        const res = await fetch('/api/admin/payment-config', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          setStripePublic(data.stripePublicKey || '');
          setPreferMockPayments(data.preferMockPayments || false);
          // We don't set the secrets back into the input fields for security
        }
      } catch (error) {
        console.error('Failed to fetch config', error);
      }
    };
    if (profile?.isAdmin && activeTab === 'config') {
      fetchConfig();
    }
  }, [profile, activeTab]);

  const handleSaveConfig = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    try {
      const token = await auth.currentUser?.getIdToken();
      const res = await fetch('/api/admin/payment-config', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          stripeSecretKey: stripeSecret,
          stripePublicKey: stripePublic,
          stripeWebhookSecret: stripeWebhookSecret,
          preferMockPayments: preferMockPayments,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setMessage('Configuration saved securely.');
        setStripeSecret('');
        setStripeWebhookSecret('');
      } else {
        setMessage(`Error: ${data.error}`);
      }
    } catch (error: any) {
      setMessage(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthReady || !profile?.isAdmin) {
    return (
      <div className="flex-1 flex justify-center items-center h-[60vh] bg-neutral-50 dark:bg-neutral-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-white"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto w-full py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Admin Dashboard - Cardston Market</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="flex items-center gap-3 mb-8">
        <ShieldAlert className="w-8 h-8 text-neutral-900 dark:text-white stroke-[1.5]" />
        <h1 className="text-3xl font-serif font-medium text-neutral-900 dark:text-white tracking-tight">Admin Dashboard</h1>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        {/* Sidebar Navigation */}
        <div className="w-full md:w-64 shrink-0">
          <nav className="flex flex-col space-y-1">
            <button
              onClick={() => setActiveTab('products')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                activeTab === 'products' 
                  ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-sm' 
                  : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
              }`}
            >
              <Package className="w-5 h-5" />
              Products
            </button>
            <button
              onClick={() => setActiveTab('transactions')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                activeTab === 'transactions' 
                  ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-sm' 
                  : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
              }`}
            >
              <Receipt className="w-5 h-5" />
              Transactions
            </button>
            <button
              onClick={() => setActiveTab('vendors')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                activeTab === 'vendors' 
                  ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-sm' 
                  : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
              }`}
            >
              <Users className="w-5 h-5" />
              Vendors
            </button>
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                activeTab === 'requests' 
                  ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-sm' 
                  : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
              }`}
            >
              <Clock className="w-5 h-5" />
              Vendor Requests
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                activeTab === 'messages' 
                  ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-sm' 
                  : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
              }`}
            >
              <MessageSquare className="w-5 h-5" />
              Support Messages
            </button>
            <button
              onClick={() => setActiveTab('config')}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                activeTab === 'config' 
                  ? 'bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 shadow-sm' 
                  : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-100 dark:hover:bg-neutral-800 hover:text-neutral-900 dark:hover:text-white'
              }`}
            >
              <Key className="w-5 h-5" />
              Payment Config
            </button>
          </nav>
        </div>

        {/* Main Content Area */}
        <div className="flex-1">
          {activeTab === 'products' && <ProductsManager />}
          {activeTab === 'transactions' && <TransactionHistory />}
          
          {activeTab === 'vendors' && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <VendorsManager onMessageClick={() => setActiveTab('messages')} />
          </div>
        )}

        {activeTab === 'requests' && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <VendorRequestsManager />
          </div>
        )}

        {activeTab === 'messages' && (
          <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <div className="mb-6">
              <h2 className="text-xl font-medium text-neutral-900 dark:text-white border-b border-neutral-200 dark:border-neutral-800 pb-4">Support Messages</h2>
              <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">Respond to vendor support inquiries.</p>
            </div>
            <Messaging role="admin" />
          </div>
        )}

        {activeTab === 'config' && (
            <div className="bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm">
              <div className="p-8 border-b border-neutral-100 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-800/50">
                <h2 className="text-xl font-medium text-neutral-900 dark:text-white flex items-center gap-2 font-serif">
                  <Key className="w-5 h-5 text-neutral-500 dark:text-neutral-400 stroke-[1.5]" />
                  Payment Configuration
                </h2>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 font-light">
                  Configure your Stripe API keys. Secrets are encrypted before being stored in Firestore.
                </p>
              </div>

              <form onSubmit={handleSaveConfig} className="p-8 space-y-8">
                {message && (
                  <div className={`p-4 rounded-xl text-sm font-medium border ${
                    message.startsWith('Error') 
                      ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-100 dark:border-red-900/50' 
                      : 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-100 dark:border-green-900/50'
                  }`}>
                    {message}
                  </div>
                )}

                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-white border-b border-neutral-100 dark:border-neutral-800 pb-2">Stripe</h3>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Public Key</label>
                      <input
                        type="text"
                        value={stripePublic}
                        onChange={(e) => setStripePublic(e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500"
                        placeholder="pk_test_..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Secret Key (Encrypted)</label>
                      <input
                        type="password"
                        value={stripeSecret}
                        onChange={(e) => setStripeSecret(e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500"
                        placeholder="sk_test_... (Leave blank to keep existing)"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">Webhook Secret (Encrypted)</label>
                      <input
                        type="password"
                        value={stripeWebhookSecret}
                        onChange={(e) => setStripeWebhookSecret(e.target.value)}
                        className="w-full px-4 py-3 border border-neutral-200 dark:border-neutral-800 rounded-xl focus:ring-1 focus:ring-neutral-900 dark:focus:ring-white focus:border-neutral-900 dark:focus:border-white bg-neutral-50 dark:bg-neutral-900 focus:bg-white dark:focus:bg-neutral-950 transition-colors text-sm text-neutral-900 dark:text-white placeholder-neutral-400 dark:placeholder-neutral-500"
                        placeholder="whsec_... (Leave blank to keep existing)"
                      />
                    </div>
                    <div className="col-span-full">
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-xl border border-blue-100 dark:border-blue-900/50">
                        <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300 mb-2">Stripe Webhook Configuration</h4>
                        <p className="text-xs text-blue-700 dark:text-blue-400 mb-2">
                          To automatically mark orders as "completed" and facilitate vendor payouts, configure a webhook endpoint in your Stripe Dashboard.
                        </p>
                        <ul className="text-xs text-blue-700 dark:text-blue-400 list-disc list-inside space-y-1 mb-2">
                          <li><strong>Endpoint URL:</strong> <code>{window.location.origin}/api/webhooks/stripe</code></li>
                          <li><strong>Events to listen to:</strong> <code>checkout.session.completed</code></li>
                        </ul>
                        <p className="text-xs text-blue-700 dark:text-blue-400">
                          After creating the webhook, copy the <strong>Signing Secret</strong> (`whsec_...`) and paste it above. For local testing using Stripe CLI: <br />
                          <code>stripe listen --forward-to {window.location.origin}/api/webhooks/stripe</code>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-6 pt-4">
                  <h3 className="text-lg font-medium text-neutral-900 dark:text-white border-b border-neutral-100 dark:border-neutral-800 pb-2">Global Settings</h3>
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="preferMockPayments"
                      checked={preferMockPayments}
                      onChange={(e) => setPreferMockPayments(e.target.checked)}
                      className="w-5 h-5 text-neutral-900 dark:text-white border-neutral-300 dark:border-neutral-700 rounded focus:ring-neutral-900"
                    />
                    <label htmlFor="preferMockPayments" className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                      Enable Mock Payments Mode (Bypass real payment gateways)
                    </label>
                  </div>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400">
                    When enabled, all checkouts will be redirected to the mock payment screen, even if real API keys are configured.
                  </p>
                </div>

                <div className="pt-8 flex justify-end border-t border-neutral-100 dark:border-neutral-800">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex items-center gap-2 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 px-8 py-3.5 rounded-xl text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors disabled:opacity-50 shadow-sm"
                  >
                    <Save className="w-4 h-4" />
                    {loading ? 'Saving...' : 'Save Configuration'}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
